/* eslint-disable jsx-a11y/media-has-caption */
import React, { useRef, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { parsePhoneNumberFromString } from 'libphonenumber-js/mobile';
import * as actions from '~/store/actions';
import {
  ringBackIrelandUK,
  ringBackUnitedStates,
  rignBackDefault,
} from '~/assets/sounds';
import * as ringSound from '~/assets/sounds';
import morningStar from '~/assets/sounds/default.wav';
import busyTone from '~/assets/sounds/busy_tone.mp3';
import endTone from '~/assets/sounds/endSound03.wav';
import bipTone from '~/assets/sounds/bipTone.wav';
import { log, isExtension } from '~/helpers';
import { VideoState } from '~/store/reducers/callCurrent';

export default function Sounds() {
  // HOOKS
  const dispatch = useDispatch();

  // SELECTORS
  const statusSounds = useSelector(state => state.system?.statusSound);
  const ringToneSystem = useSelector(state => state.system?.ringTone);
  const callsCurrent = useSelector(state => state.callCurrent?.data);
  const deviceManager = useSelector(state => state.system?.deviceManager);
  const callWaitingSound = useSelector(state => state.system?.callWaitingSound);
  const internalRingToneSystem = useSelector(
    state => state.system?.internalRingtone
  );
  const videoCall = useSelector(state => state.callCurrent?.video);

  function defineRingbackSoundToPlay(currentCalls) {
    const { numberFrom } =
      currentCalls.find(
        c => c.status === 'CALL_OUTGOING' && c.session._state === 'Establishing'
      ) ?? {};

    try {
      if (numberFrom && !isExtension(numberFrom)) {
        const { country } =
          parsePhoneNumberFromString(String(`+${numberFrom.slice(2)}`)) ?? {};

        if (country === 'IE' || country === 'UK') {
          return ringBackIrelandUK;
        }
        if (country === 'US') {
          return ringBackUnitedStates;
        }
      }
    } catch (err) {
      log.error('Error to play ringback base on country code.', err);
    }

    return rignBackDefault;
  }

  const ringbackSound = defineRingbackSoundToPlay(callsCurrent);

  // MEMO's
  const statusSoundsMemo = useMemo(() => statusSounds, [statusSounds]);
  const ringToneSystemMemo = useMemo(() => ringToneSystem, [ringToneSystem]);
  const internalRingToneSystemMemo = useMemo(() => internalRingToneSystem, [
    internalRingToneSystem,
  ]);
  const callsCurrentMemo = useMemo(() => callsCurrent, [callsCurrent]);
  // The resolved ring output: the chosen ring device while it is attached, otherwise the call
  // speaker. Never a device that is not currently connected.
  const ringerDeviceMemo = useMemo(
    () => deviceManager?.audio?.ringer?.deviceId,
    [deviceManager]
  );

  // REF's
  const ringOutgoing = useRef();
  const ringIncomming = useRef();
  const ringIncommingDouble = useRef();
  const callFinished = useRef();
  const callBusy = useRef();

  // Route the ringtone to the resolved ring device whenever it changes.
  //
  // This used to enumerate the devices itself to check the id was still connected
  // (https://nuacom.atlassian.net/browse/NWA-1012), but the check passed when EITHER the ring
  // device or the speaker was present and then preferred the ring device regardless - so an
  // unplugged ring device still reached setSinkId and threw "Requested device not found".
  // `deviceManager.audio.ringer` is already resolved against the live hardware by the
  // phoneDeviceSetup thunk, and falls back to the call speaker, so there is nothing to re-check.
  useEffect(() => {
    if (!ringerDeviceMemo) {
      return;
    }

    log.info(`[NEW PHONE DEVICE AUDIO]`);

    [ringIncomming, ringIncommingDouble].forEach(element => {
      if (typeof element?.current?.setSinkId !== 'function') {
        return;
      }

      element.current
        .setSinkId(ringerDeviceMemo)
        .catch(err => log.error('Failed to set the ringtone output.', err));
    });
  }, [ringerDeviceMemo]);

  // Play Video Ringtone
  useEffect(() => {
    if (videoCall && videoCall.state === VideoState.Initial) {
      ringIncomming.current.loop = true;
      ringIncomming.current.play();
    } else {
      ringIncomming.current.pause();
    }
  }, [videoCall]);

  useEffect(() => {
    async function handleAudioMedia() {
      try {
        // Monitor for incoming calls
        if (statusSoundsMemo && callsCurrentMemo.length < 3) {
          if (statusSoundsMemo.ringTone && callsCurrentMemo.length === 1) {
            // Check if it's an internal call
            const id = callsCurrentMemo[0]?.session.remoteIdentity.uri.user;
            const isInternalCall = id && id.length <= 4;
            ringIncomming.current.src =
              ringSound[
                isInternalCall ? internalRingToneSystemMemo : ringToneSystemMemo
              ];

            // setTimeout(async () => {
            ringIncomming.current.loop = true;
            await ringIncomming.current.play();
            // }, 150);
          } else if (statusSoundsMemo.ringTone && callWaitingSound) {
            // eslint-disable-next-line dot-notation
            ringIncomming.current.src = ringSound['beep'];
            ringIncomming.current.loop = true;
            await ringIncomming.current.play();
          } else {
            await ringIncomming.current.pause();
            ringIncomming.current.loop = false;
            await ringIncommingDouble.current.pause();
            ringIncommingDouble.current.loop = false;
          }
        }

        // Monitor for outgoing ringtone
        if (statusSoundsMemo) {
          if (statusSoundsMemo.outgoingRing) {
            // Change that
            await ringOutgoing.current.play();
            ringOutgoing.current.loop = true;
          } else {
            await ringOutgoing.current.pause();
            ringOutgoing.current.loop = false;
          }
        }

        if (statusSoundsMemo) {
          // Monitor for end tone
          if (statusSoundsMemo.endTone) {
            await callFinished.current.play();
            dispatch(actions.PlayRing('endTone', false));
          }

          // Monitor for busy tone
          if (statusSoundsMemo.busyTone) {
            await callBusy.current.play();
            dispatch(actions.PlayRing('busyTone', false));
          }
        }
      } catch (err) {
        log.error('Failed to execute the handleAudioMedia function.', err);
      }
    }

    handleAudioMedia();

    setTimeout(() => {
      handleAudioMedia();
    }, 0);
  }, [
    statusSoundsMemo,
    callsCurrentMemo,
    callWaitingSound,
    ringToneSystemMemo,
    internalRingToneSystemMemo,
    dispatch,
  ]);

  return (
    <>
      <audio src={ringbackSound} ref={ringOutgoing} />
      <audio src={morningStar} ref={ringIncomming} />
      <audio src={busyTone} ref={callBusy} />
      <audio src={endTone} ref={callFinished} />
      <audio src={bipTone} ref={ringIncommingDouble} />
    </>
  );
}
